/**
 * Character — Entity representing a player character in Wanderlight.
 *
 * Created from character creation data. Tracks attributes, skill trees,
 * skills, feats, equipment, and derived stats.
 *
 * @module Character
 */

import dataStore from '../systems/DataLoader.js';

/** Equipment slot names. */
const EQUIPMENT_SLOTS = [
  'head', 'chest', 'legs', 'boots', 'gloves',
  'weapon', 'offhand', 'ring1', 'ring2', 'amulet'
];

/** Counter for generating unique character IDs. */
let _nextId = 1;

export default class Character {
  /**
   * Create a Character from chargen data.
   * @param {object} data
   * @param {string} data.name
   * @param {string} data.raceId
   * @param {string} data.portrait
   * @param {object} data.attributes — { str, agi, int, vit, wis, cha } final values
   * @param {string} data.skillTree1 — primary skill tree ID
   * @param {string|null} [data.skillTree2] — secondary skill tree ID
   * @param {string} [data.scenarioId] — starting scenario ID
   */
  constructor(data) {
    this.id = `char_${_nextId++}`;
    this.name = data.name || 'Adventurer';
    this.raceId = data.raceId || 'human';
    this.portrait = data.portrait || null;
    this.scenarioId = data.scenarioId || null;

    // Attributes (final values including race bonuses + player allocation)
    this.attributes = {
      str: data.attributes?.str ?? 10,
      agi: data.attributes?.agi ?? 10,
      int: data.attributes?.int ?? 10,
      vit: data.attributes?.vit ?? 10,
      wis: data.attributes?.wis ?? 10,
      cha: data.attributes?.cha ?? 10,
    };

    // Skill trees (1-2)
    this.skillTrees = [];
    if (data.skillTree1) this.skillTrees.push(data.skillTree1);
    if (data.skillTree2) this.skillTrees.push(data.skillTree2);

    // Skills: map of skillId -> level (0 = not learned)
    this.skills = new Map();

    // Feats
    this.feats = [];

    // Equipment: slot -> item object
    this.equipment = {};
    for (const slot of EQUIPMENT_SLOTS) {
      this.equipment[slot] = null;
    }

    // Progression
    this.level = 1;
    this.xp = 0;
    this.unspentAttributePoints = 0;
    this.unspentSkillPoints = 0;

    // Resources (derived from attributes)
    this.hp = this.maxHp;
    this.mana = this.maxMana;

    // Gold is tracked at party level, but individual characters can hold some
    this.gold = 0;
  }

  // ── Derived Stats ──────────────────────────────────────

  /** Max HP = 50 + VIT * 5 + level * 10 */
  get maxHp() {
    let base = 50 + this.attributes.vit * 5 + this.level * 10;

    // Check for Hardy trait (Dwarf: +10% HP)
    const race = dataStore.getById('races', this.raceId);
    if (race) {
      for (const trait of race.traits) {
        if (trait.effect?.type === 'statMultiplier' && trait.effect.stat === 'maxHp') {
          base = Math.floor(base * (1 + trait.effect.value));
        }
      }
    }

    return base;
  }

  /** Max Mana = 20 + INT * 3 + WIS * 2 + level * 5 */
  get maxMana() {
    return 20 + this.attributes.int * 3 + this.attributes.wis * 2 + this.level * 5;
  }

  // ── Class Name ─────────────────────────────────────────

  /**
   * Get the class name based on selected skill trees.
   * Uses the class identity matrix from classes.json.
   * @returns {string}
   */
  getClassName() {
    if (this.skillTrees.length === 0) return 'Adventurer';
    if (this.skillTrees.length === 1) {
      return dataStore.getClassName(this.skillTrees[0]);
    }
    return dataStore.getClassName(this.skillTrees[0], this.skillTrees[1]);
  }

  // ── Attribute Modifier ─────────────────────────────────

  /**
   * Get the modifier for an attribute: (value - 10) / 2, floored.
   * @param {string} attr — attribute key (str, agi, int, vit, wis, cha)
   * @returns {number}
   */
  getAttributeModifier(attr) {
    const value = this.attributes[attr];
    if (value === undefined) return 0;
    return Math.floor((value - 10) / 2);
  }

  // ── Leveling ───────────────────────────────────────────

  /**
   * Level up the character. Grants attribute and skill points.
   */
  levelUp() {
    this.level += 1;
    this.unspentAttributePoints += 2;
    this.unspentSkillPoints += 1;

    // Recalculate max resources
    this.hp = this.maxHp;
    this.mana = this.maxMana;
  }

  // ── Skills ─────────────────────────────────────────────

  /**
   * Add a skill point to a specific skill.
   * @param {string} treeId — skill tree ID
   * @param {string} skillId — skill ID
   * @returns {boolean} — true if the point was added
   */
  addSkillPoint(treeId, skillId) {
    if (!this.skillTrees.includes(treeId)) return false;
    if (this.unspentSkillPoints <= 0) return false;

    // Look up the skill definition
    const tree = dataStore.getById('skill_trees', treeId);
    if (!tree) return false;

    const skillDef = tree.skills.find(s => s.id === skillId);
    if (!skillDef) return false;

    const currentLevel = this.skills.get(skillId) || 0;
    if (currentLevel >= skillDef.maxLevel) return false;

    this.skills.set(skillId, currentLevel + 1);
    this.unspentSkillPoints -= 1;
    return true;
  }

  // ── Equipment ──────────────────────────────────────────

  /**
   * Equip an item to a slot.
   * @param {string} slot — equipment slot name
   * @param {object} item — item object
   * @returns {object|null} — previously equipped item, or null
   */
  equipItem(slot, item) {
    if (!EQUIPMENT_SLOTS.includes(slot)) return null;
    const previous = this.equipment[slot];
    this.equipment[slot] = item;
    return previous;
  }

  /**
   * Unequip an item from a slot.
   * @param {string} slot — equipment slot name
   * @returns {object|null} — the unequipped item, or null
   */
  unequipItem(slot) {
    if (!EQUIPMENT_SLOTS.includes(slot)) return null;
    const item = this.equipment[slot];
    this.equipment[slot] = null;
    return item;
  }

  // ── Race Data ──────────────────────────────────────────

  /**
   * Get the race data object for this character.
   * @returns {object|null}
   */
  getRace() {
    return dataStore.getById('races', this.raceId);
  }

  // ── Serialization ──────────────────────────────────────

  /**
   * Serialize the character to a plain object for saving.
   * @returns {object}
   */
  toJSON() {
    return {
      id: this.id,
      name: this.name,
      raceId: this.raceId,
      portrait: this.portrait,
      scenarioId: this.scenarioId,
      attributes: { ...this.attributes },
      skillTrees: [...this.skillTrees],
      skills: Object.fromEntries(this.skills),
      feats: [...this.feats],
      equipment: { ...this.equipment },
      level: this.level,
      xp: this.xp,
      hp: this.hp,
      mana: this.mana,
      gold: this.gold,
      unspentAttributePoints: this.unspentAttributePoints,
      unspentSkillPoints: this.unspentSkillPoints,
    };
  }
}

export { EQUIPMENT_SLOTS };
